/**
 * VoiceButton — mic button for textarea voice input.
 *
 * Rendered as a WRAPPER around the textarea so the mic icon
 * floats inside the textarea at bottom-right (position: absolute).
 * This avoids collision with the iOS keyboard toolbar, which
 * appears directly above the keyboard at the position where a
 * below-textarea button would land — causing accidental taps.
 *
 * Usage:
 *   <VoiceButton onTranscript={...} accentColor="...">
 *     <textarea ... />
 *   </VoiceButton>
 *
 * Supported: Chrome, Edge, Safari (webkitSpeechRecognition).
 * Hidden on unsupported browsers (Firefox) — button not rendered.
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useVoiceInput } from '../../hooks/useVoiceInput'

interface Props {
  /** Called with each finalised transcript — append to your textarea state. */
  onTranscript: (text: string) => void
  /** Phase accent colour for the listening active state. */
  accentColor?: string
  /** The textarea (or any input element) to wrap. */
  children: ReactNode
}

export default function VoiceButton({ onTranscript, accentColor = '#9B72C0', children }: Props) {
  const { t } = useTranslation()
  const voice = useVoiceInput()

  return (
    <div style={{ position: 'relative' }}>
      {children}

      {/* Mic button — only rendered on supported browsers */}
      {voice.supported && (
        <button
          type="button"
          aria-label={voice.isListening ? t('session.voice.stop') : t('session.voice.dictate')}
          onClick={() => voice.isListening ? voice.stop() : voice.start(onTranscript)}
          style={{
            position: 'absolute',
            bottom: '0.625rem',
            right: '0.625rem',
            width: '2rem',
            height: '2rem',
            borderRadius: '50%',
            border: voice.isListening ? `1.5px solid ${accentColor}` : '1.5px solid #E4D5EE',
            background: voice.isListening ? `${accentColor}18` : 'rgba(255,255,255,0.92)',
            color: voice.isListening ? accentColor : '#9B8FAA',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.15s ease',
            // Ensure it sits above the textarea text
            zIndex: 1,
          }}
        >
          {voice.isListening ? (
            // Pulsing dot when active
            <span style={{
              width: '0.5rem',
              height: '0.5rem',
              borderRadius: '50%',
              background: accentColor,
              display: 'block',
              animation: 'pulse 1s ease infinite',
            }} />
          ) : (
            <svg width="12" height="14" viewBox="0 0 13 15" fill="none" aria-hidden="true">
              <rect x="4" y="0.5" width="5" height="8" rx="2.5" stroke="currentColor" strokeWidth="1.4" />
              <path d="M1.5 7.5a5 5 0 0 0 10 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              <line x1="6.5" y1="12.5" x2="6.5" y2="14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          )}
        </button>
      )}
    </div>
  )
}
